import { useQueryClient } from '@tanstack/react-query';
import type { StepperProps } from 'components/ui/Stepper';
import { Stepper } from 'components/ui/Stepper';
import { useCart } from 'hooks/cart/useCart';
import { useCartUI } from 'hooks/cart/useCartUI';
import { useUpdateItem } from 'hooks/cart/useUpdateItem';
import { useShopifyProductByMerchandiseIds } from 'hooks/product/useShopifyProductByMerchandiseIds';
import { useAnalytics } from 'hooks/useAnalytics';
import { useEffect, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

interface CartItemStepperProps extends Omit<StepperProps, 'handleChange'> {
  id: string;
  merchandiseId: string;
}

export function CartItemStepper({
  id,
  merchandiseId,
  quantity,
  ...rest
}: CartItemStepperProps) {
  const queryClient = useQueryClient();
  const { data: shopifyProducts } = useShopifyProductByMerchandiseIds([
    merchandiseId,
  ]);
  const product = shopifyProducts?.[0];
  const { addError } = useCartUI();

  const [inputQuantity, setInputQuantity] = useState(quantity);

  useEffect(() => {
    setInputQuantity(quantity);
  }, [quantity]);

  const { mutate: updateItem, isPending: isUpdating } =
    useUpdateItem(queryClient);

  const { data: previousCart, refetch } = useCart();
  const { analytics } = useAnalytics();

  const debounced = useDebouncedCallback((value) => {
    const debouncedValue = value as number;

    updateItem(
      {
        id,
        merchandiseId,
        quantity: debouncedValue,
      },
      {
        onError: () => {
          addError({
            id,
            message: 'Quantity could not be updated',
            errorRemovalTimeout: 6000,
          });
        },
        onSettled: () => {
          /* NOTE: We restore the previous cart quantity based on lineId
           * to account for the edge case where a two line items are in
           * the cart with the same merchandiseId, and one is incremented/
           * decremented. This can occur when a multiple of the same item
           * is in the cart, but the discount only applies to one of them.
           * Or when the quantity cannot be changed due to error.
           *
           * This can safely be cast as a number because we know the
           * lineItem exists when this component is rendered. */
          const finalQuantity = previousCart?.lines.find(
            (line) => line.id === id,
          )?.quantity;
          if (finalQuantity != null) {
            setInputQuantity(finalQuantity);
          }
        },
        onSuccess: () => {
          refetch(); /* Keep cart up-to-date and re-render cart if needed */
        },
      },
    );

    if (product) {
      if (debouncedValue > quantity) {
        analytics.addToCart([
          {
            product,
            merchandiseId,
            quantity: debouncedValue - quantity,
          },
        ]);
      } else if (debouncedValue < quantity) {
        analytics.removeFromCart([
          {
            product,
            merchandiseId,
            quantity: Math.abs(debouncedValue - quantity),
          },
        ]);
      }
    }
  }, 500);

  return (
    <Stepper
      quantity={inputQuantity}
      handleChange={(newValue) => {
        setInputQuantity(newValue);
        debounced(newValue);
      }}
      isUpdating={isUpdating}
      {...rest}
    />
  );
}
